import { useState, useEffect, useCallback } from 'react';
import { Shield, ShieldOff, RotateCcw } from 'lucide-react';
import { rpc } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Pagination } from '@/components/common/Pagination';
import { FilterBar } from '@shared/search';
import { usersSchema } from './usersSchema';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { useAuth } from '@/context/AuthContext';

interface UserWithAdmin {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  isOwner: boolean;
  termsAccepted?: boolean;
  termsStatus?: 'accepted' | 'outdated' | 'pending';
  termsAcceptedAt?: string;
  createdAt: string;
  lastLogin: string;
}

interface ListResponse {
  users: UserWithAdmin[];
  totalCount: number;
  page: number;
}

function getHashParams(): URLSearchParams {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  return new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx) : '');
}

function updateHashParams(page: number, query: string) {
  const hash = window.location.hash;
  const base = hash.includes('?') ? hash.slice(0, hash.indexOf('?')) : hash;
  const sp = new URLSearchParams();
  if (page > 1) sp.set('page', String(page));
  if (query) sp.set('q', query);
  const qs = sp.toString();
  window.history.replaceState(null, '', qs ? `${base}?${qs}` : base);
}

const PAGE_SIZE = 20;

export function UsersTab() {
  const { t } = useI18n();
  const { showToast } = useToast();
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState<UserWithAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(() => {
    const p = parseInt(getHashParams().get('page') || '1');
    return p > 0 ? p : 1;
  });
  const [query, setQuery] = useState(() => getHashParams().get('q') || '');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [promoteTarget, setPromoteTarget] = useState<UserWithAdmin | null>(null);
  const [demoteTarget, setDemoteTarget] = useState<UserWithAdmin | null>(null);
  const [resetTermsTarget, setResetTermsTarget] = useState<UserWithAdmin | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadUsers = useCallback(async (p: number, q: string) => {
    setLoading(true);
    try {
      const body: Record<string, unknown> = { page: p, pageSize: PAGE_SIZE };
      if (q) body.query = q;
      const data = await rpc<ListResponse>('ListAdminUsers', body);
      setUsers(data.users ?? []);
      setTotalCount(data.totalCount ?? 0);
      setTotalPages(Math.max(1, Math.ceil((data.totalCount ?? 0) / PAGE_SIZE)));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers(page, query);
  }, [page, query, loadUsers]);

  useEffect(() => {
    updateHashParams(page, query);
  }, [page, query]);

  useEffect(() => {
    const handleHashSync = () => {
      const p = parseInt(getHashParams().get('page') || '1');
      const q = getHashParams().get('q') || '';
      setPage(p > 0 ? p : 1);
      setQuery(q);
    };
    window.addEventListener('hashchange', handleHashSync);
    window.addEventListener('popstate', handleHashSync);
    return () => {
      window.removeEventListener('hashchange', handleHashSync);
      window.removeEventListener('popstate', handleHashSync);
    };
  }, []);

  const handleSearch = (newQuery: string) => {
    setPage(1);
    setQuery(newQuery);
  };

  const handlePromote = async () => {
    if (!promoteTarget) return;
    setActionLoading(true);
    try {
      await rpc('PromoteToAdmin', { userId: promoteTarget.id });
      showToast(t('users.promoted', { name: promoteTarget.name }));
      setPromoteTarget(null);
      loadUsers(page, query);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDemote = async () => {
    if (!demoteTarget) return;
    setActionLoading(true);
    try {
      await rpc('DemoteFromAdmin', { userId: demoteTarget.id });
      showToast(t('users.demoted', { name: demoteTarget.name }));
      setDemoteTarget(null);
      loadUsers(page, query);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetTerms = async () => {
    if (!resetTermsTarget) return;
    const target = resetTermsTarget;
    setActionLoading(true);
    try {
      await rpc('ResetUserTermsAcceptance', { userId: target.id });
      showToast(t('users.termsReset', { name: target.name }));
      setResetTermsTarget(null);
      if (currentUser?.id === target.id) {
        await refreshUser();
        return;
      }
      loadUsers(page, query);
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const renderTermsBadge = (user: UserWithAdmin) => {
    const status = user.termsStatus ?? (user.termsAccepted ? 'accepted' : 'pending');
    const acceptedAtTitle = user.termsAcceptedAt
      ? new Date(user.termsAcceptedAt).toLocaleString()
      : undefined;
    if (status === 'accepted') {
      return (
        <span
          title={acceptedAtTitle}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-green/10 text-green border border-green/20 uppercase tracking-wider"
        >
          {t('users.termsAccepted')}
        </span>
      );
    }
    if (status === 'outdated') {
      return (
        <span
          title={acceptedAtTitle}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-yellow/10 text-yellow border border-yellow/20 uppercase tracking-wider"
        >
          {t('users.termsOutdated')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-bg-surface text-text-muted border border-border-default uppercase tracking-wider">
        {t('users.termsPending')}
      </span>
    );
  };

  const countLabel = totalCount === 1 
    ? t('users.countSingular') 
    : t('users.count', { count: String(totalCount) });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-[1.4rem] font-bold text-text-primary tracking-tight">{t('users.title')}</h2>
          {!loading && totalCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-bg-surface border border-border-default text-text-muted">
              {countLabel}
            </span>
          )}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="mb-6">
        <FilterBar
          schema={usersSchema}
          initialQuery={query}
          onSearch={handleSearch}
          enableFilterUI={true}
        />
      </div>

      {loading ? (
        <div className="card overflow-hidden">
          <LoadingSpinner message={t('users.loading')} />
        </div>
      ) : error ? (
        <ErrorState message={error} />
      ) : users.length === 0 ? (
        <div className="card">
          <EmptyState icon="👥" message={t('users.empty')} />
        </div>
      ) : (
        <div className="animate-fade-in">
          <div className="card overflow-hidden">
            {/* Desktop table */}
            <table className="w-full border-collapse hidden md:table">
              <thead>
                <tr className="divider">
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('users.name')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('users.email')}
                  </th>
                  <th className="w-[180px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('users.lastLogin')}
                  </th>
                  <th className="w-[110px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('users.terms')}
                  </th>
                  <th className="w-[100px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('common.type')}
                  </th>
                  <th className="w-[100px] text-right px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted" />
                </tr>
              </thead>
              <tbody>
                {users.map((user, i) => {
                  const isSelf = currentUser?.id === user.id;

                  return (
                    <tr key={user.id} className={`hover:bg-bg-hover transition-colors ${i < users.length - 1 ? 'divider' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-text-primary truncate">{user.name}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-text-secondary truncate mono">{user.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-text-muted tabular-nums whitespace-nowrap">
                          {new Date(user.lastLogin).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {renderTermsBadge(user)}
                      </td>
                      <td className="px-4 py-3">
                        {user.isOwner ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-yellow/10 text-yellow border border-yellow/20 uppercase tracking-wider">
                            {t('users.owner')}
                          </span>
                        ) : user.isAdmin ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-accent-subtle text-accent-hover border border-accent/20 uppercase tracking-wider">
                            {t('users.admin')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-bg-surface text-text-muted border border-border-default uppercase tracking-wider">
                            {t('users.user')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(user.termsStatus ?? (user.termsAccepted ? 'accepted' : 'pending')) !== 'pending' && (
                            <button
                              onClick={() => setResetTermsTarget(user)}
                              data-testid={`reset-user-terms-${user.id}`}
                              className="p-1.5 rounded text-text-muted hover:text-yellow transition-colors cursor-pointer"
                              title={t('users.resetTerms')}
                              aria-label={t('users.resetTerms')}
                            >
                              <RotateCcw size={14} />
                            </button>
                          )}
                          {!user.isAdmin && !user.isOwner && (
                            <button
                              onClick={() => setPromoteTarget(user)}
                              className="p-1.5 rounded text-text-muted hover:text-accent transition-colors cursor-pointer"
                              title={t('users.promote')}
                              aria-label={t('users.promote')}
                            >
                              <Shield size={14} />
                            </button>
                          )}
                          {user.isAdmin && !user.isOwner && !isSelf && (
                            <button
                              onClick={() => setDemoteTarget(user)}
                              className="p-1.5 rounded text-text-muted hover:text-red transition-colors cursor-pointer"
                              title={t('users.demote')}
                              aria-label={t('users.demote')}
                            >
                              <ShieldOff size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile list */}
            <div className="md:hidden">
              {users.map((user, i) => {
                const isSelf = currentUser?.id === user.id;

                return (
                  <div key={user.id} className={`p-4 hover:bg-bg-hover transition-colors ${i < users.length - 1 ? 'divider' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 space-y-1">
                        <div className="text-sm">
                          <span className="text-text-muted text-xs">{t('users.name')}: </span>
                          <span className="text-text-primary font-medium">{user.name}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-text-muted text-xs">{t('users.email')}: </span>
                          <span className="text-text-secondary mono">{user.email}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-text-muted text-xs">{t('users.lastLogin')}: </span>
                          <span className="text-text-muted tabular-nums">{new Date(user.lastLogin).toLocaleString()}</span>
                        </div>
                        <div className="text-sm flex items-center gap-1.5 pt-0.5">
                          <span className="text-text-muted text-xs">{t('users.terms')}: </span>
                          {renderTermsBadge(user)}
                        </div>
                        <div className="text-sm flex items-center gap-1.5 pt-0.5">
                          <span className="text-text-muted text-xs">{t('common.type')}: </span>
                          {user.isOwner ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-yellow/10 text-yellow border border-yellow/20 uppercase tracking-wider">
                              {t('users.owner')}
                            </span>
                          ) : user.isAdmin ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-accent-subtle text-accent-hover border border-accent/20 uppercase tracking-wider">
                              {t('users.admin')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-bg-surface text-text-muted border border-border-default uppercase tracking-wider">
                              {t('users.user')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {(user.termsStatus ?? (user.termsAccepted ? 'accepted' : 'pending')) !== 'pending' && (
                          <button
                            onClick={() => setResetTermsTarget(user)}
                            className="p-2 rounded text-text-muted hover:text-yellow transition-colors cursor-pointer"
                            title={t('users.resetTerms')}
                            aria-label={t('users.resetTerms')}
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                        {!user.isAdmin && !user.isOwner && (
                          <button
                            onClick={() => setPromoteTarget(user)}
                            className="p-2 rounded text-text-muted hover:text-accent transition-colors cursor-pointer"
                            title={t('users.promote')}
                            aria-label={t('users.promote')}
                          >
                            <Shield size={16} />
                          </button>
                        )}
                        {user.isAdmin && !user.isOwner && !isSelf && (
                          <button
                            onClick={() => setDemoteTarget(user)}
                            className="p-2 rounded text-text-muted hover:text-red transition-colors cursor-pointer"
                            title={t('users.demote')}
                            aria-label={t('users.demote')}
                          >
                            <ShieldOff size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      <ConfirmDialog
        open={!!promoteTarget}
        onConfirm={handlePromote}
        onCancel={() => setPromoteTarget(null)}
        title={t('users.promote')}
        message={promoteTarget ? t('users.confirmPromote', { name: promoteTarget.name }) : ''}
        confirmLabel={t('users.promote')}
        loading={actionLoading}
      />

      <ConfirmDialog
        open={!!demoteTarget}
        onConfirm={handleDemote}
        onCancel={() => setDemoteTarget(null)}
        title={t('users.demote')}
        message={demoteTarget ? t('users.confirmDemote', { name: demoteTarget.name }) : ''}
        confirmLabel={t('users.demote')}
        loading={actionLoading}
      />

      <ConfirmDialog
        open={!!resetTermsTarget}
        onConfirm={handleResetTerms}
        onCancel={() => setResetTermsTarget(null)}
        title={t('users.resetTerms')}
        message={resetTermsTarget ? t('users.confirmResetTerms', { name: resetTermsTarget.name }) : ''}
        confirmLabel={t('users.resetTerms')}
        loading={actionLoading}
      />
    </div>
  );
}

